import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { mutateWithLocation, nowTimePretty } from '../helpers';

async function fetchTimeRegistrationInfo({ EmployeeId }) {
    const response = await axios.get(`api/timeregistration/GetInfo/${EmployeeId}`);
    if (response.status !== 200) throw new Error(`fetchTimeRegistrationInfo: ${response.status}`);
    return response.data;
};

async function fetchTimeRegistrationCategories() {
    const response = await axios.get('api/timeregistration/GetTimeRegistrationCategories');
    if (response.status !== 200) throw new Error(`fetchTimeRegistrationCategories: ${response.status}`);
    return response.data;
};

async function postStartTimeRegistration({ EmployeeId, CategoryName, Location }) {
    const response = await axios.post('api/timeregistration/createtimeregistration', { Id: EmployeeId, SignedIn: new Date(), CategoryName, Location });
    if (response.status !== 200) throw new Error(`postTimeRegistration: ${response.status}`);
    return response.data;
};

// TODO: CHECK dotnet for Signed in if it can be omitted
async function postStopTimeRegistration({ EmployeeId, CategoryName, Location }) {
    const response = await axios.post('/api/timeregistration/updatetimeregistration', { Id: EmployeeId, SignedOut: new Date(), CategoryName, Location });
    if (response.status !== 200) throw new Error(`stopProjectLine: ${response.status}`);
    return response.data;
};

const useEmployeeTimeClock = ({ employeeId, employeeName }) => {
    const queryClient = useQueryClient();
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar();

    const [timeClockDetails, setTimeClockDetails] = useState({
        hasClockedIn: false,
        hasClockedOut: false,
        startDateFormatted: null,
        endDateFormatted: null,
        categorySelected: '',
    });
    const [timeClockCategories, setTimeClockCategories] = useState([]);

    const [mutationSuccess, setMutationSuccess] = useState({
        start: false,
        stop: false,
        skip: false,
    });

    const showSnackbar = (message, variant) => {
        enqueueSnackbar(message, {
            variant: variant,
            autoHideDuration: 3500,
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            preventDuplicate: true,
        });
    };

    // Queries
    const { data: timeClockData, isLoading: isLoadingTimeClock, isSuccess: isSuccessTimeClock, isError: isErrorTimeClock } = useQuery(
        ['timeClock', employeeId],
        () => fetchTimeRegistrationInfo({ EmployeeId: employeeId }),
        {
            refetchOnWindowFocus: false,
            enabled: !!employeeId,
            onError: (error) => showSnackbar('Failed getting the time registration', "error"),
        }
    );
    const { data: timeClockCategoriesData, isLoading: isLoadingTimeClockCategories, isSuccess: isSuccessTimeClockCategories, isError: isErrorTimeClockCategories } = useQuery(
        'timeClockCategories', fetchTimeRegistrationCategories,
        {
            refetchOnWindowFocus: false,
            onError: (error) => showSnackbar('Failed getting the time clock categories', "error"),
        }
    );

    useEffect(() => {
        if (isSuccessTimeClockCategories && timeClockCategoriesData) {
            setTimeClockCategories(timeClockCategoriesData);
        }
    }, [isSuccessTimeClockCategories, timeClockCategoriesData]);

    useEffect(() => {
        if (isSuccessTimeClock && timeClockData?.employeeInfo) {
            const { employeeInfo, openTimeRegistrationInfo } = timeClockData;
            const isOpenTimeRegistration = openTimeRegistrationInfo && openTimeRegistrationInfo.length === 1;
            const currentRegistration = isOpenTimeRegistration ? openTimeRegistrationInfo[0] : null;

            setTimeClockDetails(prev => ({
                ...prev,
                hasClockedIn: employeeInfo?.isCheckedIn ?? false,
                startDateFormatted: isOpenTimeRegistration ? new Date(currentRegistration?.checkIn).toLocaleString("de-GE") : null,
                categorySelected: currentRegistration?.category?.name || employeeInfo.defaultCategory?.name || '',
            }));
        }
        if (mutationSuccess.start) {
            setTimeClockDetails(prev => ({
                ...prev,
                hasClockedIn: true,
                hasClockedOut: false,
                startDateFormatted: nowTimePretty(),
                endDateFormatted: null
            }));
        }
        if (mutationSuccess.stop) {
            setTimeClockDetails(prev => ({
                ...prev,
                hasClockedIn: false,
                hasClockedOut: true,
                startDateFormatted: null,
                endDateFormatted: nowTimePretty()
            }));
        }
    }, [isSuccessTimeClock, timeClockData, mutationSuccess]);

    // Mutations
    const { mutate: startTimeRegistrationMutation, isLoading: isLoadingStartTimeClock } = useMutation(
        postStartTimeRegistration,
        {
            onSuccess: () => {
                showSnackbar(`Clocked in at ${nowTimePretty()}`, "success");
                queryClient.invalidateQueries(['timeClock', employeeId]);
                history.replace('/');
            },
            onError: (error) => {
                showSnackbar("Error clocking in", "error")
            },
        }
    );
    const { mutate: stopTimeRegistrationMutation, isLoading: isLoadingStopTimeClock } = useMutation(
        postStopTimeRegistration,
        {
            onSuccess: () => {
                showSnackbar(`Clocked out at ${nowTimePretty()}`, "success");
                queryClient.invalidateQueries(['timeClock', employeeId]);
                history.replace('/');
            },
            onError: (error) => {
                showSnackbar("Error clocking out", "error")
            },
        }
    );

    // Wrappers
    const startTimeClock = async (EmployeeId, CategoryName) => {
        mutateWithLocation(startTimeRegistrationMutation, { EmployeeId, CategoryName })
            .then(() => { setMutationSuccess((prev) => ({ ...prev, start: true, stop: false })); })
            .catch((message) => { showSnackbar(message, 'error'); });
    };
    const stopTimeClock = async (EmployeeId, CategoryName) => {
        mutateWithLocation(stopTimeRegistrationMutation, { EmployeeId, CategoryName })
            .then(() => { setMutationSuccess((prev) => ({ ...prev, stop: true, start: false })); })
            .catch((message) => { showSnackbar(message, 'error'); });
    };

    return {
        timeClockCategories,
        timeClockDetails,
        isLoading: isLoadingTimeClock || isLoadingTimeClockCategories,
        isError: isErrorTimeClock || isErrorTimeClockCategories,
        isSuccess: isSuccessTimeClock || isSuccessTimeClockCategories,
        startTimeClock,
        isLoadingStartTimeClock,
        stopTimeClock,
        isLoadingStopTimeClock
    };
};

export default useEmployeeTimeClock;